import svgFileInvoiceDollar from '@static/icons/file-invoice-dollar.svg?raw';
import svgTrashCan from '@static/icons/trash-can.svg?raw';

import {
    genId,
    unixTimestampToDateTimeLocal,
    unixTimestampToReadableDate,
    isBlankValue,
    isElementInViewport,
    highlightOnFirstClick,
    centimetersToInches,
} from '@/js/util.js';

import {
    assertEquals,
    assertIn,
    isFloatStrict,
    isIntStrict,
    parseFloatStrict,
    parseIntStrict
} from '@/js/util/asserts.js';

import {
    getAddressFromCoords,
    getCustomAttribName,
    getSpeciesData,
} from '@/js/util/shared_functionality.js';

import {
    convertDbhFromDisplayToStored,
    convertDbhFromStoredToDisplay
} from '@/js/util/tree_functionality.js';

import { Button } from './modal.js';

import { captureException as sentryCaptureException } from '@sentry/browser';
import { escapeRegExp } from 'lodash-es';


const attribute_classes = {};

attribute_classes.AttributeBase = class {
    constructor(opts) {
        assertIn('attribute', opts);
        assertIn('value', opts);

        this.attribute = opts['attribute'];
        this.value = opts['value'];
        this.files = opts['files'] ?? {};
        this.is_new = opts['is_new'] ?? false;
        this.other_attributes = opts['other_attributes'] ?? {};
        this.other_values = opts['other_values'] ?? [];

        assertIn('attribute_type', this.attribute);
        assertIn('category_name', this.attribute);
        assertIn('name', this.attribute);
        assertIn('internal_name', this.attribute);

        this.type = this.attribute['attribute_type'];
        this.category = this.attribute['category_name'];
        this.name = this.attribute['name'];
        this.internal_name = this.attribute['internal_name'];
        this.repeatable = this.attribute['repeatable'] ?? false;
        this.required = this.attribute['required'] ?? false;
        this.hidden = this.attribute['hidden'] ?? false;
        this.disabled = this.attribute['readOnly'] ?? false;
        if(this.is_new && isBlankValue(this.value)) { this.disabled = false; }

        this.container_elem = null;
        this.input_components_elem = null;
        this.validation_text_elem = null;
        this.input_elems = [];

        if(this.category === 'custom') {
            this.name = getCustomAttribName(this.name);
        }

        if('keyOrder' in this.attribute) {
            assertEquals(this.attribute['keyOrder'].length, Object.keys(this.attribute['options']).length);
            const key_order_copy = [...this.attribute['keyOrder']].toSorted();
            const options_keys_copy = [...Object.keys(this.attribute['options'])].toSorted();
            assertEquals(JSON.stringify(key_order_copy), JSON.stringify(options_keys_copy));
        }
    }

    setup() {
        this.init();

        const container_elem = document.createElement('div');
        container_elem.setAttribute('data-category', this.category);
        container_elem.setAttribute('data-attribute-type', this.type);
        container_elem.classList.add('attribute');
        container_elem.classList.add(this.repeatable ? 'repeatable' : 'not-repeatable');
        container_elem.classList.add(this.required ? 'required' : 'not-required');
        if(this.hidden) container_elem.style.display = 'none';
        if(this.attribute['preventClearing']) { container_elem.classList.add('prevent-clearing'); }
        if(this.attribute['preventClearingOnce']) { container_elem.classList.add('prevent-clearing-once'); }
        this.container_elem = container_elem;

        /* Create attribute heading */ {
            const attribute_heading_elem = document.createElement('div');
            attribute_heading_elem.classList.add('d-flex', 'justify-content-between');

            const attribute_label_container_elem = document.createElement('span');

            const label_elem = document.createElement('span');
            label_elem.classList.add('h6', 'fw-bold');
            label_elem.innerText = this.name;
            attribute_label_container_elem.appendChild(label_elem);

            if(this.required) {
                const required_elem = document.createElement('span');
                required_elem.classList.add('h6', 'fw-bold', 'red', 'mx-1');
                required_elem.innerText = '*';
                attribute_label_container_elem.appendChild(required_elem);
            }

            attribute_heading_elem.appendChild(attribute_label_container_elem);

            const hint_elem = document.createElement('span');
            hint_elem.classList.add('hint-text');
            hint_elem.innerText = ((this.attribute['unit']) ? ('('+this.attribute['unit']+')') : '');
            attribute_heading_elem.appendChild(hint_elem);

            container_elem.appendChild(attribute_heading_elem);
        }

        if('subname' in this.attribute) {
            const subname_elem = document.createElement('div');
            subname_elem.classList.add('subname-text');
            subname_elem.innerText = this.attribute['subname'];
            container_elem.appendChild(subname_elem);
        }

        const input_components_elem = document.createElement('div');
        input_components_elem.classList.add('input-components');
        container_elem.appendChild(input_components_elem);
        this.input_components_elem = input_components_elem;

        const validation_text_elem = document.createElement('span');
        validation_text_elem.classList.add('validation-text');
        container_elem.appendChild(validation_text_elem);
        this.validation_text_elem = validation_text_elem;


        if(this.repeatable) {
            const repeatable_add_button_elem = document.createElement('button');
            repeatable_add_button_elem.classList.add('repeatable-add-button', 'btn', 'btn-sm', 'btn-outline-secondary', 'mt-2');
            repeatable_add_button_elem.innerText = 'Add Value';
            const button_hints = [];
            if(this.attribute['minRepeated']) { button_hints.push('min '+this.attribute['minRepeated']); }
            if(this.attribute['maxRepeated']) { button_hints.push('max '+this.attribute['maxRepeated']); }
            if(button_hints.length > 0) { repeatable_add_button_elem.innerText += ' ('+button_hints.join(', ')+')'; }
            repeatable_add_button_elem.type = 'button';

            container_elem.appendChild(repeatable_add_button_elem);

            repeatable_add_button_elem.addEventListener('click', () => {
                if(this.attribute['maxRepeated'] && (this.input_elems.length+1 > this.attribute['maxRepeated'])) {
                    alert('The '+this.name+' attribute only allows a maximum of '+this.attribute['maxRepeated']+' repeated values.');
                    return;
                }

                this.addInputComponent('');
            });
        }

        if(this.repeatable && (this.value === undefined || this.value === null)) {
            // No values, dont add any inputs
        } else if(this.repeatable && Array.isArray(this.value)) {
            for(const inner_value of this.value) { this.addInputComponent(inner_value); }
        } else {
            this.addInputComponent(this.value);
        }

        return container_elem;
    }

    // Abstract
    init() {}

    // Abstract
    createInputElem(value) {
        const input_elem = document.createElement('input');
        input_elem.type = 'text';
        input_elem.classList.add('input');
        input_elem.value = (value === undefined || value === null) ? '' : value;
        if(this.disabled) { input_elem.disabled = true; }
        if(this.attribute['placeholder']) { input_elem.placeholder = this.attribute['placeholder']; }
        if(this.attribute['selectOnClick']) { highlightOnFirstClick(input_elem); }
        return input_elem;
    }

    addInputComponent(value) {

        const input_component_elem = document.createElement('div');
        input_component_elem.classList.add('input-component');

        const inputs_elem = document.createElement('div');
        inputs_elem.classList.add('inputs');
        const input_elem = this.createInputElem(value);
        inputs_elem.appendChild(input_elem);
        this.input_elems.push(input_elem);

        input_component_elem.appendChild(inputs_elem);

        if(this.repeatable) {
            const repeatable_remove_button_elem = document.createElement('button');
            repeatable_remove_button_elem.classList.add('repeatable-remove-button', 'flex-center');
            repeatable_remove_button_elem.innerHTML = svgTrashCan;

            repeatable_remove_button_elem.type = 'button';
            input_component_elem.appendChild(repeatable_remove_button_elem);

            repeatable_remove_button_elem.addEventListener('click', () => {
                this.input_elems = this.input_elems.filter(elem => elem !== input_elem);
                input_component_elem.remove();
            });
        }

        this.input_components_elem.appendChild(input_component_elem);
    }

    // Abstract
    getValueFromInputElem(input_elem) {
        return input_elem.value;
    }

    // Abstract
    validateValue(value) {
        return value;
    }

    _validateRequired(value) {
        if(this.required && isBlankValue(value)) {
            if(this.repeatable) { throw new Error('This attribute cannot have empty values.'); }
            throw new Error('This attribute cannot be empty.');
        }
    }

    validate() {
        if(this.repeatable) {
            const values = [];
            for(const input_elem of this.input_elems) {
                const value = this.getValueFromInputElem(input_elem);
                if(isBlankValue(value) && !this.required) { continue; }
                values.push(this.validateValue(value));
            }
            if(this.required && values.length === 0) { throw new Error('This attribute is required'); }
            if(this.attribute['minRepeated'] && values.length < this.attribute['minRepeated']) { throw new Error('At least '+this.attribute['minRepeated']+' are required.'); }
            return values;
        } else {
            assertEquals(this.input_elems.length, 1);
            const input_elem = this.input_elems[0];
            const value = this.getValueFromInputElem(input_elem);
            this._validateRequired(value);
            return this.validateValue(value);
        }
    }

    scrollTo(block) {
        this.container_elem.scrollIntoView({ behavior: 'smooth', block: block ?? 'center' });
    }

    setValidationText(text) {
        this.validation_text_elem.innerText = text;
    }
}

attribute_classes.StringAttribute = class extends attribute_classes.AttributeBase {
    validateValue(value) {
        if(!value) { return ''; }
        if(value === '') { return ''; }
        if(value.length < this.attribute['minLen']) { throw new Error('Must be at least ' + this.attribute['minLen'] + ' letters long'); }
        if(value.length > this.attribute['maxLen']) { throw new Error('Cannot exceed ' + this.attribute['maxLen'] + ' letters long'); }
        return value;
    }
}

attribute_classes.BigStringAttribute = class extends attribute_classes.StringAttribute {
    createInputElem(value) {
        const input_elem = document.createElement('textarea');
        input_elem.classList.add('input');
        input_elem.rows = 4;
        input_elem.value = (value === undefined || value === null) ? '' : value;
        if(this.disabled) { input_elem.disabled = true; }
        if(this.attribute['placeholder']) { input_elem.placeholder = this.attribute['placeholder']; }
        if(this.attribute['selectOnClick']) { highlightOnFirstClick(input_elem); }
        return input_elem;
    }
}

attribute_classes.IdAttribute = class extends attribute_classes.StringAttribute {}

attribute_classes.ImageAttribute = class extends attribute_classes.IdAttribute {
    init() {
        this.images_added = {};
    }

    createInputElem(value) {
        const wrapper = document.createElement('div');

        let input_elem;
        if(value && value !== '') {
            input_elem = document.createElement('input');
            input_elem.disabled = true;
            input_elem.type = 'text';
            wrapper.appendChild(input_elem);
        } else {
            input_elem = document.createElement('input');
            input_elem.type = 'file';
            if(this.disabled) { input_elem.disabled = true; }

            input_elem.onchange = (() => {
                const file = input_elem.files[0];
                if(file) {
                    img_preview.src = URL.createObjectURL(file);
                    img_preview.style.display = 'block';
                    label.innerText = 'File Selected';
                    label.classList.add('disabled');
                }
            });
        }

        input_elem.classList.add('hidden');
        input_elem.id = 'e' + genId();
        wrapper.appendChild(input_elem);

        const label = document.createElement('label');
        label.setAttribute('for', input_elem.id);
        label.classList.add('btn', 'btn-info', 'w-100');
        label.innerText = 'Upload';
        if(this.disabled) { label.classList.add('disabled'); }
        wrapper.appendChild(label);

        const img_preview = document.createElement('img');
        img_preview.style.maxWidth = '100%';
        img_preview.style.marginLeft = 'auto';
        img_preview.style.marginRight = 'auto';
        img_preview.style.marginTop = '10px';
        img_preview.style.display = 'none';
        wrapper.appendChild(img_preview);

        if(value && value !== '') {
            input_elem.value = value;

            let image_url = 'static/davey_institute_no_tree.png';
            let upload_message = 'File ID: '+value;

            try {
                if(this.files.hasFile(value)) {
                    const blob = this.files.getFileBlob(value);
                    const file_data = this.files.getFileData(value);
                    image_url = URL.createObjectURL(blob);
                    upload_message = 'Uploaded '+unixTimestampToReadableDate(file_data.timestamp_uploaded);
                    // URL is revoked in AssetForm.cleanup
                } else {
                    console.error('File `'+value+'` not present in file cache.');
                }
            } catch(err) {
                console.log('Error getting ImageAttribute blob: ', err);
                sentryCaptureException(err);
            }

            img_preview.src = image_url;
            img_preview.style.display = 'block';
            img_preview.title = upload_message;

            label.innerText = upload_message;
            label.classList.add('disabled');
            label.disabled = true;
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const input_elem = wrapper.querySelector('input');

        if(input_elem.type==='text') {
            return input_elem.value;

        } else {
            return input_elem.files[0];

        }
    }

    validateValue(value) {
        if(value instanceof Blob) {
            if(!value.type.startsWith('image/')) { throw new Error('Selected file was not an image.'); }
            const new_photo_id = genId();
            this.images_added[new_photo_id] = value;
            return new_photo_id;
        }
        return value;
    }
}

attribute_classes.NearestAddressAttribute = class extends attribute_classes.StringAttribute {
    createInputElem(value) {

        const wrapper_elem = document.createElement('div');

        const input_elem = super.createInputElem(value)
        wrapper_elem.appendChild(input_elem);

        const button_container = document.createElement('div');
        button_container.classList.add('d-flex', 'justify-content-center', 'gap-2');
        wrapper_elem.appendChild(button_container);

        const trash_button = document.createElement('button');
        trash_button.classList.add('d-flex', 'align-items-center', 'btn', 'btn-outline-danger');
        trash_button.style.padding = '4px';
        trash_button.innerHTML = svgTrashCan + ' Clear';
        button_container.appendChild(trash_button);

        trash_button.addEventListener('click', () => {
            input_elem.value = '';
        });

        const find_button = new Button('Find Nearest Address', ['btn-outline-info']);
        find_button.onClick(async () => {
            find_button.disable();
            find_button.startLoading();
            const coords = this.other_values['coordinates'];
            if(isBlankValue(coords)) {
                input_elem.value = 'Missing coordinates';
                find_button.stopLoading();
                find_button.enable();
                return;
            }

            input_elem.value = await getAddressFromCoords({ 'lng': coords[0], 'lat': coords[1] });
            find_button.stopLoading();
            find_button.enable();
        });
        button_container.appendChild(find_button.elem);

        return wrapper_elem;
    }

    getValueFromInputElem(wrapper) {
        const input_elem = wrapper.querySelector('input');
        return input_elem.value;
    }
}

attribute_classes.NumberAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        const input_elem = super.createInputElem(value);
        input_elem.inputMode = 'numeric';
        return input_elem;
    }

    validateValue(value) {
        if(value === '') { return ''; }
        if(Number.isNaN(value)) { throw new Error('Not a valid number'); }
        if(!isFloatStrict(value)) { throw new Error('Must be a number'); }
        const floatVal = parseFloatStrict(value);

        if(floatVal < this.attribute['minVal']) { throw new Error('Must be at least ' + this.attribute['minVal']); }
        if(floatVal > this.attribute['maxVal']) { throw new Error('Cannot exceed ' + this.attribute['maxVal']); }

        return floatVal;
    }
}

attribute_classes.IntegerAttribute = class extends attribute_classes.NumberAttribute {
    validateValue(value) {
        if(value === '') { return ''; }

        super.validateValue(value);

        if(!isIntStrict(value)) { throw new Error('Must be a whole number'); }
        return parseIntStrict(value);
    }
}

attribute_classes.SliderNumberAttribute = class extends attribute_classes.NumberAttribute {
    createInputElem(value) {
        const container_elem = document.createElement('div');
        container_elem.classList.add('slider-container', 'd-flex', 'align-items-center');

        const minVal = this.attribute['minVal'] !== undefined ? this.attribute['minVal'] : 0;
        const maxVal = this.attribute['maxVal'] !== undefined ? this.attribute['maxVal'] : 100;
        const stepVal = this.attribute['step'] !== undefined ? this.attribute['step'] : 'any';

        const min_elem = document.createElement('span');
        min_elem.classList.add('me-2', 'small', 'text-muted');
        min_elem.innerText = minVal;
        container_elem.appendChild(min_elem);

        const slider_elem = document.createElement('input');
        slider_elem.type = 'range';
        slider_elem.classList.add('flex-grow-1');
        slider_elem.min = minVal;
        slider_elem.max = maxVal;
        slider_elem.step = stepVal;
        slider_elem.value = (isBlankValue(value) ? minVal : value);
        if(this.disabled) { slider_elem.disabled = true; }
        container_elem.appendChild(slider_elem);

        const max_elem = document.createElement('span');
        max_elem.classList.add('ms-2', 'small', 'text-muted');
        max_elem.innerText = maxVal;
        container_elem.appendChild(max_elem);

        const display_elem = document.createElement('input');
        display_elem.type = 'number';
        display_elem.classList.add('ms-3');
        display_elem.style.width = '75px';
        display_elem.min = minVal;
        display_elem.max = maxVal;
        display_elem.step = stepVal;
        display_elem.value = slider_elem.value;
        if(this.disabled) { display_elem.disabled = true; }
        slider_elem.addEventListener('input', () => { display_elem.value = slider_elem.value; });
        display_elem.addEventListener('input', () => { slider_elem.value = display_elem.value; });
        container_elem.appendChild(display_elem);

        return container_elem;
    }

    getValueFromInputElem(container_elem) {
        const display_elem = container_elem.querySelector('input[type="number"]');
        return display_elem ? display_elem.value : '';
    }
}

attribute_classes.SliderIntegerAttribute = class extends attribute_classes.SliderNumberAttribute {
    createInputElem(value) {
        const container_elem = super.createInputElem(value);

        const slider_elem = container_elem.querySelector('input[type="range"]');
        const display_elem = container_elem.querySelector('input[type="number"]');

        // Apply the integer step restriction to BOTH the slider and the number box
        if(!this.attribute['step']) {
            slider_elem.step = '1';
            if(display_elem) { display_elem.step = '1'; }
        }

        return container_elem;
    }

    validateValue(value) {
        if(value === '') { return ''; }
        super.validateValue(value);
        if(!isIntStrict(value)) { throw new Error('Must be a whole number'); }
        return parseIntStrict(value);
    }
}

attribute_classes.TimestampAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        const input_elem = document.createElement('input');
        input_elem.type = 'datetime-local';
        input_elem.classList.add('input');

        if(value && value !== '') { input_elem.value = unixTimestampToDateTimeLocal(value); }

        if(this.disabled) { input_elem.disabled = true; }
        return input_elem;
    }

    validateValue(value) {
        if(value === '') { return ''; }

        const timestamp = (+new Date(value)) / 1000;
        if(isNaN(timestamp)) { throw new Error('Must be a valid date & time.'); }

        if(timestamp < this.attribute['minVal']) { throw new Error('Must be at least ' + unixTimestampToDateTimeLocal(this.attribute['minVal']) + '.'); }
        if(timestamp > this.attribute['maxVal']) { throw new Error('Cannot exceed ' + unixTimestampToDateTimeLocal(this.attribute['maxVal']) + '.'); }

        return timestamp;
    }
}

attribute_classes.DateAttribute = class extends attribute_classes.TimestampAttribute {
    createInputElem(value) {
        const input_elem = document.createElement('input');
        input_elem.type = 'date';
        input_elem.classList.add('input');

        if(value && value !== '') {
            const fullDateTime = unixTimestampToDateTimeLocal(value);
            input_elem.value = fullDateTime.split('T')[0];
        }

        if(this.disabled) { input_elem.disabled = true; }
        return input_elem;
    }

    validateValue(value) {
        if(value === '') { return ''; }

        const localDateString = value.includes('T') ? value : `${value}T00:00:00`;
        const timestamp = (+new Date(localDateString)) / 1000;

        if(isNaN(timestamp)) { throw new Error('Must be a valid date.'); }

        if(timestamp < this.attribute['minVal']) {
            const minDate = unixTimestampToDateTimeLocal(this.attribute['minVal']).split('T')[0];
            throw new Error('Must be at least ' + minDate + '.');
        }
        if(timestamp > this.attribute['maxVal']) {
            const maxDate = unixTimestampToDateTimeLocal(this.attribute['maxVal']).split('T')[0];
            throw new Error('Cannot exceed ' + maxDate + '.');
        }

        return timestamp;
    }
}

attribute_classes.DoubleNumberAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        let value1 = '';
        let value2 = '';

        if(Array.isArray(value)) {
            if(!isBlankValue(value[0])) { value1 = value[0]; }
            if(!isBlankValue(value[0])) { value2 = value[1]; }
        }

        const wrapper_elem = document.createElement('div');
        
        const input1_elem = document.createElement('input');
        input1_elem.classList.add('input');
        input1_elem.type = 'number';
        input1_elem.value = value1;
        wrapper_elem.appendChild(input1_elem);

        const input2_elem = document.createElement('input');
        input2_elem.classList.add('input');
        input2_elem.type = 'number';
        input2_elem.value = value2;
        wrapper_elem.appendChild(input2_elem);

        if(this.disabled) {
            input1_elem.disabled = true;
            input2_elem.disabled = true;
        }

        return wrapper_elem;
    }

    getValueFromInputElem(wrapper) {
        const inputs = wrapper.querySelectorAll('input');
        return [inputs[0].value, inputs[1].value];
    }

    validateValue(value) {
        if(!Array.isArray(value)) { throw new Error('Values must be a list'); }
        let value1 = value[0];
        let value2 = value[1];

        if(value1==='' && value2==='') { return []; }
        if(value1==='' || value2==='') { throw new Error('Must fill in both values'); }


        if(!isFloatStrict(value1)) { throw new Error('First value must be a number'); }
        if(!isFloatStrict(value2)) { throw new Error('Second value must be a number'); }

        value1 = parseFloatStrict(value1);
        value2 = parseFloatStrict(value2);

        if(value1 < this.attribute['minVal1']) { throw new Error('First value must be at least ' + this.attribute['minVal1']); }
        if(value1 > this.attribute['maxVal1']) { throw new Error('First value cannot exceed ' + this.attribute['maxVal1']); }
        if(value2 < this.attribute['minVal2']) { throw new Error('Second value must be at least ' + this.attribute['minVal2']); }
        if(value2 > this.attribute['maxVal2']) { throw new Error('Second value cannot exceed ' + this.attribute['maxVal2']); }

        return [value1, value2];
    }
}

attribute_classes.ProportionAttribute = class extends attribute_classes.DoubleNumberAttribute {
    createInputElem(value) {
        const wrapper = super.createInputElem(value);
        wrapper.classList.add('d-flex', 'gap-2', 'align-items-center');

        for(const input_elem of wrapper.querySelectorAll('.input')) {
            input_elem.classList.add('inline-input');
        }

        const seperator_elem = document.createElement('div');
        seperator_elem.textContent = 'of';
        wrapper.lastElementChild.before(seperator_elem);

        return wrapper;
    }
}

attribute_classes.SingleOptionAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {

        const wrapper = document.createElement('div');
        const group_name = 'e' + genId();
        let option_idx = 0;

        for(const option of this.attribute['options']) {
            const option_id = group_name + '_' + (option_idx++);
            const div = document.createElement('div');

            const input = document.createElement('input');
            input.type = 'radio';
            input.id = option_id;
            input.name = group_name;
            input.value = option;
            if(value === option) { input.checked = true; }
            if(this.disabled) { input.disabled = true; }
            div.appendChild(input);

            const label = document.createElement('label');
            label.classList.add('user-select-none');
            label.htmlFor = option_id;
            label.innerText = option;
            div.appendChild(label);

            wrapper.appendChild(div);
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const checked = wrapper.querySelector('input:checked');
        return checked ? checked.value : '';
    }

    validateValue(value) {
        if(value === '') { return ''; }
        if(!this.attribute['options'].includes(value)) { throw new Error('Must be in options'); }
        return value;
    }
}

attribute_classes.ButtonSingleOptionAttribute = class extends attribute_classes.SingleOptionAttribute {
    createInputElem(value) {
        const wrapper = super.createInputElem(value);
        if(this.attribute['neverWrap']) { wrapper.classList.add('never-wrap-buttons'); }
        addNavigabilityToLabels(wrapper);
        addUncheckabilityToRadioButtons(wrapper);
        return wrapper;
    }
}

attribute_classes.MultiOptionAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        value = Array.isArray(value) ? value : [];

        const wrapper = document.createElement('div');
        const group_name = genId();

        let idx = 0;
        for(const option of this.attribute['options']) {
            const option_id = group_name + '_' + (idx++);
            const div = document.createElement('div');

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.id = option_id;
            input.value = option;
            if(value.includes(option)) { input.checked = true; }
            if(this.disabled) { input.disabled = true; }
            div.appendChild(input);

            const label = document.createElement('label');
            label.classList.add('user-select-none');
            label.htmlFor = option_id;
            label.innerText = option;
            div.appendChild(label);

            wrapper.appendChild(div);
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const checkboxes = wrapper.querySelectorAll('input:checked');
        const values = [];
        checkboxes.forEach(c => values.push(c.value));
        return values;
    }

    validateValue(value) {
        if(!Array.isArray(value)) { throw new Error('Values must be a list'); }

        for(const inner_value of value) {
            if(!this.attribute['options'].includes(inner_value)) { throw new Error('Invalid option: ' + inner_value); }
        }

        return value;
    }
}

attribute_classes.ButtonMultiOptionAttribute = class extends attribute_classes.MultiOptionAttribute {
    createInputElem(value) {
        const wrapper = super.createInputElem(value);
        if(this.attribute['neverWrap']) { wrapper.classList.add('never-wrap-buttons'); }
        addNavigabilityToLabels(wrapper);
        return wrapper;
    }
}

attribute_classes.ExpandingButtonMultiOptionAttribute = class extends attribute_classes.MultiOptionAttribute {
    _refreshValuesListInputElem(value) {
        value = value ?? [];
        if(value.length === 0) {
            this.values_list_input_elem.classList.add('hidden');
        } else {
            this.values_list_input_elem.classList.remove('hidden');
            this.values_list_input_elem.value = value.toString().replaceAll(',', ', ');
        }
    }

    createInputElem(value) {
        const inner_input_elem = super.createInputElem(value);
        addNavigabilityToLabels(inner_input_elem);
        inner_input_elem.classList.add('hidden');

        const wrapper_elem = document.createElement('div');
        wrapper_elem.appendChild(inner_input_elem);

        const values_list_input_elem = document.createElement('textarea');
        values_list_input_elem.disabled = true;
        values_list_input_elem.classList.add('input');
        this.values_list_input_elem = values_list_input_elem;
        this._refreshValuesListInputElem(value);
        wrapper_elem.appendChild(values_list_input_elem);

        const edit_values_button = document.createElement('button');
        edit_values_button.classList.add('btn', 'btn-outline-secondary', 'lone-centered');
        edit_values_button.type = 'button';
        edit_values_button.innerText = 'Edit Values';
        wrapper_elem.appendChild(edit_values_button);

        edit_values_button.addEventListener('click', () => {
            values_list_input_elem.classList.add('hidden');
            edit_values_button.classList.add('hidden');
            inner_input_elem.classList.remove('hidden');
            finished_editing_button.classList.remove('hidden');
        });

        const finished_editing_button = document.createElement('button');
        finished_editing_button.classList.add('btn', 'btn-success', 'hidden', 'lone-centered');
        finished_editing_button.style.width = '80%';
        finished_editing_button.innerText = 'Finished Editing Values';
        wrapper_elem.appendChild(finished_editing_button);

        finished_editing_button.addEventListener('click', ()=> {
            values_list_input_elem.classList.remove('hidden');
            edit_values_button.classList.remove('hidden');
            inner_input_elem.classList.add('hidden');
            finished_editing_button.classList.add('hidden');
            const value = this.getValueFromInputElem(inner_input_elem);
            this._refreshValuesListInputElem(value);
            if(!isElementInViewport(this.container_elem)) { this.scrollTo('start'); }
        });

        return wrapper_elem;
    }
}

attribute_classes.DropdownOptionAttribute = class extends attribute_classes.SingleOptionAttribute {
    createInputElem(value) {
        const select_elem = document.createElement('select');
        select_elem.classList.add('input');
        if(!this.required) { select_elem.appendChild(new Option('', '')); }

        for(const option of this.attribute['options']) {
            const opt = document.createElement('option');
            opt.value = option;
            opt.text = option;
            if(value === option) { opt.selected = true; }
            select_elem.appendChild(opt);
        }
        if(this.disabled) { select_elem.disabled = true; }

        return select_elem;
    }

    getValueFromInputElem(input_elem) {
        return input_elem.value;
    }
}

attribute_classes.DoubleDropdownAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        const value1 = (Array.isArray(value)) ? value[0] : '';
        const value2 = (Array.isArray(value)) ? value[1] : '';

        const wrapper = document.createElement('div');

        const select1_elem = document.createElement('select');
        select1_elem.classList.add('input');
        if(!this.required) { select1_elem.appendChild(new Option('', '')); }
        this.attribute['options1'].forEach(o => select1_elem.appendChild(new Option(o, o, false, o === value1)));
        wrapper.appendChild(select1_elem);

        const select2_elem = document.createElement('select');
        select2_elem.classList.add('input');
        if(!this.required) { select2_elem.appendChild(new Option('', '')); }
        this.attribute['options2'].forEach(o => select2_elem.appendChild(new Option(o, o, false, o === value2)));
        wrapper.appendChild(select2_elem);

        if(this.disabled) {
            select1_elem.disabled = true;
            select2_elem.disabled = true;
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const selects = wrapper.querySelectorAll('select');
        return [selects[0].value, selects[1].value];
    }

    validateValue(value) {
        const value1 = value[0];
        const value2 = value[1];
        if(value1 === '' && value2 === '') { return ['', '']; }
        if(value1 === '' || value2 === '') { throw new Error('Both options must be selected.'); }
        if(!this.attribute['options1'].includes(value1)) { throw new Error('Invalid first option'); }
        if(!this.attribute['options2'].includes(value2)) { throw new Error('Invalid second option'); }
        return [value1, value2];
    }
}

attribute_classes.SearchDropdownAttribute = class extends attribute_classes.AttributeBase {
    makeDropdownItemNavigable(dropdown_item_elem, input_elem) {
        dropdown_item_elem.onkeydown = (e) => {
            if(e.key === 'Enter') {
                e.preventDefault();
                dropdown_item_elem.click();
            } else if(e.key === 'ArrowDown') {
                e.preventDefault();
                if(dropdown_item_elem.nextElementSibling) { dropdown_item_elem.nextElementSibling.focus(); }
            } else if(e.key === 'ArrowUp') {
                e.preventDefault();
                if(dropdown_item_elem.previousElementSibling) {
                    dropdown_item_elem.previousElementSibling.focus();
                } else {
                    input_elem.focus();
                }
            }
        };
    }

    createInputElem(value) {
        const MAX_SEARCH_DROPDOWN_ITEMS = 500;

        const wrapper = document.createElement('div');
        wrapper.className = 'input position-relative';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'w-100';
        input.placeholder = this.attribute['placeholder'] ?? 'Search...';
        if(this.attribute['selectOnClick']) { highlightOnFirstClick(input); }
        input.value = value || '';
        if(this.disabled) { input.disabled = true; }
        wrapper.appendChild(input);

        const dropdown = document.createElement('div');
        dropdown.className = 'search-dropdown-items w-100';
        wrapper.appendChild(dropdown);

        const showDropdown = () => { dropdown.style.display = 'block'; }
        const hideDropdown = () => { dropdown.style.display = 'none'; }

        const options = this.getOptions();

        input.addEventListener('keydown', (e) => {
            if(e.key === 'ArrowDown' && dropdown.firstElementChild && !dropdown.firstElementChild.classList.contains('disabled')) {
                e.preventDefault();
                dropdown.firstElementChild.focus();
            }
        });

        input.addEventListener('input', () => {
            const value = input.value.toLowerCase();
            if(value.length === 0) { hideDropdown(); return; }

            let filtered = options.filter(item => item.toLowerCase().includes(value));
            if(filtered.length > MAX_SEARCH_DROPDOWN_ITEMS) {
                const div = document.createElement('div');
                div.classList.add('search-dropdown-item', 'disabled');
                div.innerText = 'Too many matches';
                dropdown.replaceChildren(div);

            } else if(filtered.length > 0) {
                dropdown.replaceChildren();
                for(const item of filtered) {
                    const div = document.createElement('div');
                    div.className = 'search-dropdown-item';
                    div.tabIndex = 0;
                    div.innerText = item;
                    div.title = item;
                    div.onclick = () => {
                        input.value = item;
                        hideDropdown();
                    };
                    this.makeDropdownItemNavigable(div, input);
                    dropdown.appendChild(div);
                }

            } else {
                const div = document.createElement('div');
                div.classList.add('search-dropdown-item', 'disabled');
                div.innerText = 'No matches';
                dropdown.replaceChildren(div);

            }

            showDropdown();
        });

        document.addEventListener('click', (e) => {
             if(!wrapper.contains(e.target)) { hideDropdown(); }
        });

        input.addEventListener('focus', () => {
             if(input.value.length > 0 && !options.includes(input.value)) { showDropdown(); }
        });

        return wrapper;
    }

    getOptions() {
        return this.attribute['options'];
    }

    getValueFromInputElem(wrapper) {
        return wrapper.querySelector('input').value;
    }

    validateValue(value) {
        if(value === '') { return ''; }
        if(!this.getOptions().includes(value)) { throw new Error('Must be in options'); }
        return value;
    }
}

attribute_classes.KeyedSingleOptionAttribute = class extends attribute_classes.SingleOptionAttribute {
    createInputElem(value) {
        const wrapper = document.createElement('div');
        const group_name = 'e' + genId();
        let idx = 0;

        if(!('keyOrder' in this.attribute)) { this.attribute['keyOrder'] = Object.keys(this.attribute['options']); }
        for(const key of this.attribute['keyOrder']) {
            const labelText = this.attribute['options'][key];
            const id = group_name + '_' + (idx++);
            const inner_wrapper = document.createElement('div');

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = group_name;
            input.id = id;
            input.value = key;
            if(value === key) { input.checked = true; }
            inner_wrapper.appendChild(input);

            const label = document.createElement('label');
            label.classList.add('user-select-none');
            label.htmlFor = id;
            label.innerText = labelText;
            inner_wrapper.appendChild(label);

            wrapper.appendChild(inner_wrapper);
        }
        return wrapper;
    }

    validateValue(value) {
        if(value === '') { return ''; }
        if(!(value in this.attribute['options'])) { throw new Error('Invalid option key'); }
        return value;
    }
}

attribute_classes.KeyedDropdownOptionAttribute = class extends attribute_classes.KeyedSingleOptionAttribute {
    createInputElem(value) {
        const select_elem = document.createElement('select');
        select_elem.classList.add('input');

        if(!this.required) { select_elem.appendChild(new Option('', '')); }

        if(!('keyOrder' in this.attribute)) { this.attribute['keyOrder'] = Object.keys(this.attribute['options']); }
        for(const key of this.attribute['keyOrder']) {
            const labelText = this.attribute['options'][key];
            const opt = document.createElement('option');

            opt.value = key;
            opt.text = labelText;

            if(value === key) { opt.selected = true; }

            select_elem.appendChild(opt);
        }

        if(this.disabled) { select_elem.disabled = true; }

        return select_elem;
    }

    getValueFromInputElem(input_elem) {
        return input_elem.value;
    }
}

attribute_classes.KeyedSearchDropdownAttribute = class extends attribute_classes.SearchDropdownAttribute {
    createInputElem(valueKey) {
        const label = (valueKey in this.attribute['options']) ? this.attribute['options'][valueKey] : valueKey;
        return super.createInputElem(label);
    }

    getOptions() {
        return Object.values(this.attribute['options']);
    }

    getValueFromInputElem(wrapper) {
        const label = wrapper.querySelector('input').value;
        if(label === '') { return ''; }

        const key = Object.keys(this.attribute['options']).find(k => this.attribute['options'][k] === label);
        if(!key) { return label; }
        return key;
    }

    validateValue(valueKey) {
        if(valueKey === '') { return ''; }
        if(!(valueKey in this.attribute['options'])) { throw new Error('Invalid option selected'); }
        return valueKey;
    }
}

attribute_classes.SpeciesSearchDropdownAttribute = class extends attribute_classes.KeyedSearchDropdownAttribute {
    init() {
        const options_expanded = [];
        const options_expanded_lookup = {};
        const text_representation_lookup = {};
        const common_name_lookup = {};
        const species_code_lookup = new Set();
        this.custom_tree_ids = new Set();

        for(const [species_code, species_data_arr] of Object.entries(this.attribute['options'])) {
            const species_data = getSpeciesData(species_data_arr);
            let scientific_name = species_data['scientific_name'];
            let common_name = species_data['common_name'];
            let species_type = species_data['species_type'];
            let leaf_type = species_data['leaf_type'];
            let percent_leaf_type = species_data['percent_leaf_type'];
            let show = species_data['show'];
            let engine_compat = species_data['engine_compat'];
            let text_representation = (common_name+' | '+scientific_name);
            const common_name_lower = common_name.toLowerCase();
            const scientific_name_lower = scientific_name.toLowerCase();

            const option_expanded = {
                'SpeciesCode': species_code,
                'ScientificName': scientific_name,
                'CommonName': common_name,
                'SpeciesType': species_type,
                'LeafType': leaf_type ?? '',
                'TextRepresentation': text_representation,
                'SearchString': common_name_lower+' '+scientific_name_lower+' '+(species_code.toLowerCase()),
                'ScientificNameLower': scientific_name_lower,
                'CommonNameLower': common_name_lower,
                'SpeciesRepresentation': (species_type[0]+'.'+species_code),
                'EngineCompat': engine_compat,
                'PercentLeafType': percent_leaf_type,
            };

            if(species_type === 'Pinned') {
                option_expanded['ScientificName'] = '';
                option_expanded['SpeciesRepresentation'] = '';
                option_expanded['TextRepresentation'] = common_name;
                text_representation = common_name;
            }

            if(!show) { option_expanded['SearchString'] = ''; }

            species_code_lookup.add(species_code.toLowerCase());
            options_expanded.push(option_expanded);
            options_expanded_lookup[species_code] = option_expanded;
            text_representation_lookup[text_representation] = species_code;
            common_name_lookup[common_name.toLowerCase()] = species_code;
        }

        if('custom_tree_species' in window.newcollector.settings) {
            for(const species_data_arr of window.newcollector.settings['custom_tree_species']) {
                const species_code = genId(8);
                const common_name =         species_data_arr[0];
                const scientific_name =     species_data_arr[1];
                const species_type =        species_data_arr[2];
                const text_representation = (common_name+' | '+scientific_name);
                const common_name_lower = common_name.toLowerCase();
                const scientific_name_lower = scientific_name.toLowerCase();

                const option_expanded = {
                    'SpeciesCode': species_code,
                    'ScientificName': scientific_name,
                    'CommonName': common_name,
                    'SpeciesType': species_type,
                    'LeafType': '',
                    'TextRepresentation': text_representation,
                    'SearchString': common_name_lower+' '+scientific_name_lower,
                    'ScientificNameLower': scientific_name_lower,
                    'CommonNameLower': common_name_lower,
                    'SpeciesRepresentation': species_type[0],
                    'EngineCompat': false,
                    'PercentLeafType': '',
                    'Serialized': 'custom_species:'+common_name+':'+scientific_name+':'+species_type,
                };

                species_code_lookup.add(species_code.toLowerCase());
                options_expanded.push(option_expanded);
                options_expanded_lookup[species_code] = option_expanded;
                text_representation_lookup[text_representation] = species_code;
                common_name_lookup[common_name.toLowerCase()] = species_code;
                this.custom_tree_ids.add(species_code);
            }
        }


        // Remove blank values from lookup structures
        species_code_lookup.delete('');
        text_representation_lookup[''] = '';
        common_name_lookup[''] = '';

        this.species_code_lookup = species_code_lookup;
        this.options_expanded = options_expanded;
        this.options_expanded_lookup = options_expanded_lookup;
        this.text_representation_lookup = text_representation_lookup;
        this.common_name_lookup = common_name_lookup;
        if('pinned_tree_species' in newcollector.settings && newcollector.settings['pinned_tree_species'].length > 0) {
            const pinned_custom_species_codes = new Set();
            for(const custom_species_data of newcollector.settings['pinned_tree_species']) {
                const parts = custom_species_data.split(':');
                pinned_custom_species_codes.add(text_representation_lookup[parts[1]+' | '+parts[2]]);
            }

             this.pinned_options = [
                 ...this.options_expanded.filter(option => (newcollector.settings['pinned_tree_species'].includes(option['SpeciesCode']))),
                 ...this.options_expanded.filter(option => pinned_custom_species_codes.has(option['SpeciesCode'])),
             ];

        } else {
            this.pinned_options = this.options_expanded.filter(option => (option['SpeciesType'] === 'Pinned'));
        }
    }

    createInputElem(value) {
        const MAX_SEARCH_DROPDOWN_ITEMS = 500;

        const wrapper = document.createElement('div');
        wrapper.className = 'input position-relative';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'w-100';
        input.placeholder = this.attribute['placeholder'] ?? 'Search...';
        if(this.attribute['selectOnClick']) { highlightOnFirstClick(input); }

        if(value in this.options_expanded_lookup) {
            const current_item = this.options_expanded_lookup[value];
            input.value = current_item['TextRepresentation'];

        } else if(value && value.startsWith('custom_species:')) {
            const parts = value.split(':');
            input.value = parts[1]+' | '+parts[2];

        } else {
            input.value = (value ?? '');
        }

        if(this.disabled) { input.disabled = true; }
        wrapper.appendChild(input);

        const dropdown = document.createElement('div');
        dropdown.className = 'search-dropdown-items w-100';
        wrapper.appendChild(dropdown);

        const showDropdown = () => { dropdown.style.display = 'block'; }
        const hideDropdown = () => { dropdown.style.display = 'none'; }

        const genItemElem = ((item, search_words) => {
            const wrapper = document.createElement('div');
            wrapper.classList.add('search-dropdown-item');
            wrapper.tabIndex = 0;
            wrapper.onclick = () => {
                input.value = item['TextRepresentation'];
                hideDropdown();
            };
            this.makeDropdownItemNavigable(wrapper, input);

            const safe_search_words = search_words
                .map(word => escapeRegExp(word))
                .sort((a, b) => b.length - a.length);

            const highlight_regex = new RegExp(`(${safe_search_words.join('|')})`, 'gi');

            /* Line 1 */ {
                const line1_elem = document.createElement('div');
                line1_elem.classList.add('d-flex', 'justify-content-between');
                wrapper.appendChild(line1_elem);

                const common_name_elem = document.createElement('div');
                common_name_elem.innerHTML = item['CommonName'].replace(highlight_regex, '<b>$1</b>');
                common_name_elem.title = item['CommonName'];
                line1_elem.appendChild(common_name_elem);

                const species_code_elem = document.createElement('div');
                species_code_elem.innerText = item['SpeciesRepresentation'];
                line1_elem.appendChild(species_code_elem);
            }

            /* Line 1 */ {
                const line2_elem = document.createElement('div');
                line2_elem.classList.add('d-flex', 'justify-content-between');
                wrapper.appendChild(line2_elem);

                const scientific_name_elem = document.createElement('div');
                scientific_name_elem.classList.add('fst-italic');
                scientific_name_elem.innerHTML = item['ScientificName'].replace(highlight_regex, '<b>$1</b>');
                scientific_name_elem.title = item['ScientificName'];
                line2_elem.appendChild(scientific_name_elem);

                const leaf_type_elem = document.createElement('div');
                leaf_type_elem.style.fontSize = '85%';

                if(item['PercentLeafType']) {
                    leaf_type_elem.innerText += item['PercentLeafType'];
                    if(item['LeafType']) { leaf_type_elem.innerText += ', '; }
                }

                if(item['LeafType']) {
                    leaf_type_elem.innerText += item['LeafType'];
                }

                if(!item['EngineCompat']) {
                    leaf_type_elem.innerHTML += `
                        <svg class="fa-icon" style="color: #ef7318; width: 17px; height: 17px; vertical-align: text-bottom;">
                            <title>Not compatible with i-Tree benefits!</title>
                            ${svgFileInvoiceDollar}
                        </svg>
                    `;
                }

                line2_elem.appendChild(leaf_type_elem);
            }

            return wrapper;
        });

        const showItems = ((filtered, search_words) => {
            if(filtered.length > MAX_SEARCH_DROPDOWN_ITEMS) {
                const div = document.createElement('div');
                div.classList.add('search-dropdown-item', 'disabled');
                div.innerText = 'Too many matches';
                dropdown.replaceChildren(div);

            } else if(filtered.length > 0) {
                dropdown.replaceChildren();

                const all_options = [];
                const result_order = ['Pinned', 'Class', 'Genus', 'Species'];
                for(const species_type of result_order) {
                    const options = filtered.filter(option => (option['SpeciesType'] === species_type));
                    if(species_type === 'Genus') {
                        options.sort((optionA, optionB) => optionA['SpeciesCode'].length - optionB['SpeciesCode'].length);
                    } else {
                        options.sort((optionA, optionB) => optionA['CommonName'].localeCompare(optionB['CommonName']));
                    }
                    all_options.push(...options);
                }

                const other_options = filtered.filter(option => !result_order.includes(option['SpeciesType']));
                all_options.push(...other_options);

                /* Add options to dropdown as needed */ {
                    const INITIAL_NUM_OPTIONS = 30;
                    const ADD_OPTIONS_IN_BATCHES_OF = 10;
                    const ADD_OPTIONS_WHEN_WITHIN_N_PX_OF_BOTTOM = 75;
                    let render_index = 0;

                    const addOptions = ((num_to_add) => {
                        const fragment = document.createDocumentFragment();
                        const end = Math.min(render_index + num_to_add, all_options.length);
                        for(; render_index < end; render_index++) {
                            fragment.appendChild(genItemElem(all_options[render_index], search_words));
                        }
                        dropdown.appendChild(fragment);
                    });

                    addOptions(INITIAL_NUM_OPTIONS);

                    dropdown.onscroll = (() => {
                        if(dropdown.scrollTop + dropdown.clientHeight >= dropdown.scrollHeight - ADD_OPTIONS_WHEN_WITHIN_N_PX_OF_BOTTOM) {
                            if(render_index < all_options.length) {
                                addOptions(ADD_OPTIONS_IN_BATCHES_OF);
                            }
                        }
                    });
                }


            } else {
                const div = document.createElement('div');
                div.classList.add('search-dropdown-item', 'disabled');
                div.innerText = 'No matches';
                dropdown.replaceChildren(div);

            }
        });

        input.addEventListener('input', () => {
            const value = input.value.toLowerCase().trim();

            if(value.length === 0) {
                showItems(this.pinned_options, []);
                showDropdown();
                return;
            }

            const search_words = value.split(/\s+/);
            let filtered = this.options_expanded.filter(option => {
                return search_words.every(word => option['SearchString'].includes(word));
            });

            showItems(filtered, search_words);
            showDropdown();
        });

        document.addEventListener('click', (e) => {
            if(!wrapper.contains(e.target)) {
                const current_key = this.getKeyFromValue(input.value);
                if(current_key !== '' && (current_key in this.options_expanded_lookup)) { input.value = this.options_expanded_lookup[current_key]['TextRepresentation']; }
                hideDropdown();
            }
        });

        input.addEventListener('keydown', (e) => {
            if(e.key === 'Enter') {
                const current_key = this.getKeyFromValue(input.value);
                if(current_key !== '') {
                    if(current_key in this.options_expanded_lookup) { input.value = this.options_expanded_lookup[current_key]['TextRepresentation']; }
                    hideDropdown();
                }
            } else if(e.key === 'ArrowDown') {
                if(dropdown.firstElementChild && !dropdown.firstElementChild.classList.contains('disabled')) {
                    e.preventDefault();
                    dropdown.firstElementChild.focus();
                }
            }
        });

        input.addEventListener('focus', () => {
            if(input.value.length === 0) {
                showItems(this.pinned_options, []);
                showDropdown();

            } else {
                if(this.getKeyFromValue(input.value) === '') {
                    showDropdown();
                }
            }
        });

        return wrapper;
    }

    getKeyFromValue(value) {
        if(!value) { return ''; }

        if(value in this.text_representation_lookup) {
            // Value is a generated text representation
            return this.text_representation_lookup[value];
        }

        const value_lower = value.toLowerCase();

        if(this.species_code_lookup.has(value_lower)) {
            // Value is a species code
            return value.toUpperCase();
        }

        if(value_lower in this.common_name_lookup) {
            // Value is an exact common name
            return this.common_name_lookup[value_lower];
        }

        return '';
    }

    getValueFromInputElem(wrapper) {
        return this.getKeyFromValue(wrapper.querySelector('input').value);
    }

    validateValue(valueKey) {
        if(valueKey === '') { return ''; }
        if(this.custom_tree_ids.has(valueKey)) { return this.options_expanded_lookup[valueKey]['Serialized']; }
        if(!(valueKey in this.attribute['options'])) { throw new Error('Invalid option selected'); }
        return valueKey;
    }
}

attribute_classes.KeyedButtonSingleOptionAttribute = class extends attribute_classes.KeyedSingleOptionAttribute {
    createInputElem(value) {
        const wrapper = super.createInputElem(value);
        if(this.attribute['neverWrap']) { wrapper.classList.add('never-wrap-buttons'); }
        addNavigabilityToLabels(wrapper);
        addUncheckabilityToRadioButtons(wrapper);
        return wrapper;
    }
}

attribute_classes.KeyedMultiOptionAttribute = class extends attribute_classes.AttributeBase {
    createInputElem(value) {
        value = Array.isArray(value) ? value : [];

        const wrapper = document.createElement('div');
        const group_name = genId();

        let idx = 0;
        if(!('keyOrder' in this.attribute)) { this.attribute['keyOrder'] = Object.keys(this.attribute['options']); }
        for(const key of this.attribute['keyOrder']) {
            const labelText = this.attribute['options'][key];
            const option_id = group_name + '_' + (idx++);
            const div = document.createElement('div');

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.id = option_id;
            input.value = key;
            if(value.includes(key)) { input.checked = true; }
            if(this.disabled) { input.disabled = true; }
            div.appendChild(input);

            const label = document.createElement('label');
            label.classList.add('user-select-none');
            label.htmlFor = option_id;
            label.innerText = labelText;
            div.appendChild(label);

            wrapper.appendChild(div);
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const checkboxes = wrapper.querySelectorAll('input:checked');
        const values = [];
        checkboxes.forEach(c => values.push(c.value));
        return values;
    }

    validateValue(value) {
        if(!Array.isArray(value)) { throw new Error('Values must be a list'); }

        for(const inner_value of value) {
            if(!(inner_value in this.attribute['options'])) { throw new Error('Invalid option key: ' + inner_value); }
        }

        return value;
    }
}

attribute_classes.KeyedButtonMultiOptionAttribute = class extends attribute_classes.KeyedMultiOptionAttribute {
    createInputElem(value) {
        const wrapper = super.createInputElem(value);
        if(this.attribute['neverWrap']) { wrapper.classList.add('never-wrap-buttons'); }
        addNavigabilityToLabels(wrapper);
        return wrapper;
    }
}

attribute_classes.PolygonCoordinatesAttribute = class extends attribute_classes.BigStringAttribute {
    createInputElem(value) {
        let display_value = value;
        if(Array.isArray(value)) { display_value = value.length === 0 ? '' : JSON.stringify(value, null, 2); }
        return super.createInputElem(display_value);
    }

    validateValue(value) {
        if(value === '') {  return []; }

        let parsedValue;
        const validated_polygon = [];
        try {
            parsedValue = JSON.parse(value);
        } catch (e) {
            throw new Error('Polygon coordinates must be valid JSON.');
        }

        if(!Array.isArray(parsedValue)) { throw new Error('Polygon coordinates must be a list.'); }
        if(parsedValue.length === 0) { throw new Error('Polygon must have at least one ring (the exterior ring).'); }

        for(let ring_idx = 0; ring_idx < parsedValue.length; ring_idx++) {
            const ring = parsedValue[ring_idx];
            const validated_ring = [];

            if(!Array.isArray(ring)) { throw new Error(`Ring at index ${ring_idx} must be a list of coordinate pairs.`); }
            if(ring.length < 4) { throw new Error(`Ring at index ${ring_idx} must have at least 4 coordinates to be closed.`); }

            for(let coord_idx = 0; coord_idx < ring.length; coord_idx++) {
                const coord = ring[coord_idx];
                if(!Array.isArray(coord) || coord.length !== 2) { throw new Error(`Coordinate at ring ${ring_idx}, index ${coord_idx} must be a [lng, lat] pair.`); }

                let lng = coord[0];
                let lat = coord[1];

                if(typeof lng === 'string' && !isFloatStrict(lng)) { throw new Error(`Longitude at ring ${ring_idx}, index ${coord_idx} must be a number.`); }
                if(typeof lat === 'string' && !isFloatStrict(lat)) { throw new Error(`Latitude at ring ${ring_idx}, index ${coord_idx} must be a number.`); }

                lng = parseFloatStrict(lng);
                lat = parseFloatStrict(lat);

                if(lng < -180.0 || lng > 180.0) { throw new Error(`Longitude ${lng} at ring ${ring_idx}, index ${coord_idx} must be between -180 and 180.`); }
                if(lat < -90.0 || lat > 90.0) { throw new Error(`Latitude ${lat} at ring ${ring_idx}, index ${coord_idx} must be between -90 and 90.`); }

                if(Number.isInteger(lng)) { lng = parseIntStrict(lng); }
                if(Number.isInteger(lat)) { lat = parseIntStrict(lat); }

                validated_ring.push([lng, lat]);
            }

            const firstCoord = validated_ring[0];
            const lastCoord = validated_ring[validated_ring.length - 1];
            if(firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
                throw new Error(`Ring at index ${ring_idx} is not closed. The first and last coordinates must match exactly.`);
            }

            validated_polygon.push(validated_ring);
        }

        return validated_polygon;
    }
}

attribute_classes.DbhAttribute = class extends attribute_classes.NumberAttribute {
    init() {
        const settings = window.newcollector.settings;
        this.attribute['unit'] = (settings.units==='Metric' ? 'centimeters' : 'inches')+', '+settings.trunk_measurement.toLowerCase();
    }

    createInputElem(value) {
        const elem = super.createInputElem(null);
        if(isFloatStrict(value)) {
            elem.value = convertDbhFromStoredToDisplay(value);
        }
        return elem;
    }

    getValueFromInputElem(input_elem) {
        let value = input_elem.value;
        if(isFloatStrict(value)) {
            value = convertDbhFromDisplayToStored(parseFloatStrict(value));
        }
        return value;
    }

    validateValue(value) {
        if(value === '') { return ''; }
        if(Number.isNaN(value)) { throw new Error('Not a valid number'); }
        if(!isFloatStrict(value)) { throw new Error('Must be a number'); }
        const floatVal = parseFloatStrict(value);

        const settings = window.newcollector.settings;
        if(settings.units==='Metric') {
            if(floatVal < this.attribute['minVal']) { throw new Error('Must be at least ' + this.attribute['minVal'] + ' cm'); }
            if(floatVal > this.attribute['maxVal']) { throw new Error('Cannot exceed ' + this.attribute['maxVal'] + ' cm'); }
        } else {
            if(floatVal < this.attribute['minVal']) { throw new Error('Must be at least ' + centimetersToInches(this.attribute['minVal']) + ' in'); }
            if(floatVal > this.attribute['maxVal']) { throw new Error('Cannot exceed ' + centimetersToInches(this.attribute['maxVal']) + ' in'); }
        }

        return floatVal;
    }
}

attribute_classes.CustomSpeciesAttribute = class extends attribute_classes.AttributeBase {
    static species_types = ['Class', 'Genus', 'Species'];

    createInputElem(value) {
        const value_is_array = Array.isArray(value);
        const common_name =         value_is_array ? value[0] : '';
        const scientific_name =     value_is_array ? value[1] : '';
        const species_type =        value_is_array ? value[2] : '';

        const wrapper = document.createElement('div');

        const common_name_input = document.createElement('input');
        common_name_input.classList.add('input');
        common_name_input.placeholder = 'Common Name';
        common_name_input.value = common_name;
        wrapper.appendChild(common_name_input);

        const scientific_name_input = document.createElement('input');
        scientific_name_input.classList.add('input');
        scientific_name_input.placeholder = 'Scientific Name';
        scientific_name_input.value = scientific_name;
        wrapper.appendChild(scientific_name_input);

        const species_type_select = document.createElement('select');
        species_type_select.classList.add('input');
        for(const option of attribute_classes.CustomSpeciesAttribute.species_types) {
            species_type_select.appendChild(new Option(option, option, false, option===species_type));
        }
        wrapper.appendChild(species_type_select);

        if(this.disabled) {
            common_name_input.disabled = true;
            scientific_name_input.disabled = true;
            species_type_select.disabled = true;
        }

        return wrapper;
    }

    getValueFromInputElem(wrapper) {
        const inputs = wrapper.querySelectorAll('input,select');
        return [inputs[0].value, inputs[1].value, inputs[2].value];
    }

    validateValue(value) {
        const value1 = value[0];
        const value2 = value[1];
        const value3 = value[2];
        if(value1 === '' && value2 === '' && value3 === '') { return ['', '']; }
        if(value1 === '' || value2 === '' || value3 === '') { throw new Error('All three options must be filled in.'); }
        if(!attribute_classes.CustomSpeciesAttribute.species_types.includes(value3)) { throw new Error('Invalid species type'); }
        return [value1, value2, value3];
    }
}

export const getAttributeObj = (opts) => {
    const attribute_type = opts['attribute']['attribute_type'];
    if(attribute_type in attribute_classes) { return new attribute_classes[attribute_type](opts); }
    console.warn('Attribute not implemented:', attribute_type);
    return new attribute_classes.AttributeBase(opts);
}

function addNavigabilityToLabels(wrapper) {
    for(const label of wrapper.querySelectorAll('label')) {
        label.tabIndex = 0;
        label.addEventListener('keydown', (e) => {
            if(e.key === 'Enter') {
                label.click();
                e.stopPropagation();
            }
        });
    }
}

function addUncheckabilityToRadioButtons(wrapper) {
    for(const label of wrapper.querySelectorAll('label')) {
        label.addEventListener('click', (e) => {
            const associated_radio_button = label.parentNode.children[0];
            if(associated_radio_button.checked) {
                associated_radio_button.checked = false;
                e.preventDefault();
                associated_radio_button.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }
}